import {
  List,
  TextInput,
  SelectInput,
  DatagridConfigurable,
  SelectColumnsButton,
  ExportButton,
  TopToolbar,
  FunctionField,
  WrapperField,
  useListContext,
} from "react-admin";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { HiOutlineInformationCircle, HiOutlineX } from "react-icons/hi";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { RaRowActions } from "../../components/RaRowActions";
import { AppBulkDeleteButton } from "../../components/AppBulkDeleteButton";
import { ListPageIndicator } from "../../components/TablePageIndicator";
import { determineUserReadableRole } from "../users/UserUtils";
import { colors, fontFamilyMono, radius, shadows } from "../../data/tokens";
import {
  OrphanReasonChip,
  formatOrphanMatricula,
} from "./orphaned-users-util";

// Estilo compacto de tabela — espelho do datagridSx das listas de
// Sócios e Transações
const datagridSx = {
  "& .RaDatagrid-headerCell": {
    fontSize: 11.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: colors.textMuted,
    backgroundColor: colors.bgSubtle,
    paddingTop: 1,
    paddingBottom: 1,
  },
  "& .RaDatagrid-rowCell": { paddingTop: 0.75, paddingBottom: 0.75 },
};

// Os valores casam por "contém" no servidor com as razões reais da
// base: CONTA PROVISÓRIA, CONTA DUPLICADA VIDE, DUPLICATED EMAIL e
// DUPLICATED CPF
const reasonChoices = [
  { id: "PROV", name: "Conta provisória" },
  { id: "VIDE", name: "Duplicada (VIDE)" },
  { id: "email", name: "E-mail duplicado" },
  { id: "cpf", name: "CPF duplicado" },
];

const postFilters = [
  <TextInput label="Nome" source="sNome" alwaysOn />,
  <TextInput label="Matrícula" source="nMatricula" alwaysOn />,
  <TextInput label="ID legado" source="oldId" alwaysOn />,
  <SelectInput
    source="issueReason"
    label="Razão do erro"
    choices={reasonChoices}
    emptyText="Todas"
    helperText={false}
    alwaysOn
  />,
];

// Limpa todos os filtros ativos; só aparece quando existe filtro aplicado
const ClearAllFiltersButton = () => {
  const { filterValues, setFilters } = useListContext();
  if (!filterValues || Object.keys(filterValues).length === 0) return null;
  return (
    <Button
      size="small"
      color="secondary"
      startIcon={<HiOutlineX size={16} />}
      onClick={() => setFilters({}, [])}
    >
      Limpar filtros
    </Button>
  );
};

export const OrphanedUsersList = () => {
  const isAdmin =
    determineUserReadableRole(localStorage.getItem("roles")) === "admin";

  const ListActions = () => (
    <TopToolbar sx={{ alignItems: "center" }}>
      <SelectColumnsButton preferenceKey="orphaned-users.datagrid.v2" />
      <ExportButton />
      <ClearAllFiltersButton />
      {/* navegação ancorada à direita; botões ficam à esquerda */}
      <span style={{ marginLeft: "auto", display: "flex" }}>
        <ListPageIndicator />
      </span>
    </TopToolbar>
  );

  return (
    <>
      <PageTopBreadcrumb
        title="Membros Órfãos"
        firstPath="/#/orphaned-users/crud"
        actionType="create"
      />
      <List
        title=" "
        filters={postFilters}
        actions={<ListActions />}
        perPage={25}
        // Mantém a página atual visível enquanto a próxima carrega
        queryOptions={{ keepPreviousData: true }}
        sx={{
          // Mesmo padrão visual das listas de Sócios e Transações:
          // filtros em grade regular dentro do cartão branco (antes a
          // lista vivia num Card próprio com a linha flex padrão do
          // react-admin)
          "& .RaList-main": {
            backgroundColor: colors.white,
            borderRadius: `${radius.lg}px`,
            boxShadow: shadows.card,
            padding: "12px 20px 8px",
          },
          "& .RaList-content": {
            boxShadow: "none",
            backgroundColor: "transparent",
          },
          "& .RaList-actions": {
            display: "flex",
            flexDirection: "column-reverse",
            alignItems: "stretch",
            gap: 1,
          },
          "& .RaList-actions .MuiToolbar-root": {
            position: "static",
            gap: 1,
            width: "100%",
            justifyContent: "flex-start",
            minHeight: "auto !important",
            paddingBottom: 0,
          },
          "& .RaList-actions > span:empty": { display: "none" },
          // Filtros em linha única elástica
          "& .RaList-actions form": {
            width: "100%",
            alignItems: "center",
            rowGap: 1.5,
            columnGap: 1.5,
            paddingBottom: 1,
            paddingTop: 0,
            minHeight: "auto",
          },
          "& .RaFilterForm-filterFormInput": {
            display: "flex",
            alignItems: "center",
            position: "relative",
            marginTop: "0 !important",
            marginBottom: "0 !important",
            flex: "1 1 170px",
            minWidth: 150,
            maxWidth: 320,
          },
          "& .RaFilterForm-filterFormInput .MuiFormControl-root": {
            width: "100%",
            marginTop: "0 !important",
            marginBottom: "0 !important",
          },
          // Botão ⊗ como badge no canto superior direito do campo
          "& .RaFilterFormInput-hideButton": {
            position: "absolute",
            top: -10,
            right: -10,
            zIndex: 1,
            padding: 0,
            marginTop: "0 !important",
            marginBottom: "0 !important",
            color: colors.textMuted,
            backgroundColor: colors.white,
            borderRadius: "50%",
            "& svg": { fontSize: 22 },
            "&:hover": {
              color: colors.action,
              backgroundColor: colors.white,
            },
          },
          "& .RaFilterFormInput-spacer": { display: "none" },
        }}
      >
        {/* preferenceKey versionada (v2): o menu "⋮" antigo, sem rótulo,
            deixava "Unlabeled column" gravado nas preferências de quem
            já tinha aberto o seletor de colunas */}
        <DatagridConfigurable
          rowClick="show"
          sx={datagridSx}
          preferenceKey="orphaned-users.datagrid.v2"
          bulkActionButtons={<AppBulkDeleteButton />}
        >
          <FunctionField
            label="Nome"
            sortBy="sNome"
            render={(record: any) => (
              <Typography
                component="span"
                title={record.sNome || ""}
                sx={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: colors.primaryDark,
                  display: "block",
                  maxWidth: 260,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {record.sNome || "—"}
              </Typography>
            )}
          />
          <FunctionField
            label="CPF"
            sortBy="nCPF"
            render={(record: any) => (
              <Typography
                component="span"
                sx={{
                  fontSize: 13,
                  fontFamily: fontFamilyMono,
                  whiteSpace: "nowrap",
                }}
              >
                {record.nCPF || "—"}
              </Typography>
            )}
          />
          {/* Muitos e-mails do legado vieram em CAIXA ALTA — minúsculas
              apenas na exibição (CSS); o dado gravado fica intacto */}
          <FunctionField
            label="E-mail"
            sortBy="sEmailPart"
            render={(record: any) => (
              <Typography
                component="span"
                title={record.sEmailPart || ""}
                sx={{
                  fontSize: 13,
                  color: colors.textMuted,
                  textTransform: "lowercase",
                  display: "block",
                  maxWidth: 260,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {record.sEmailPart || "—"}
              </Typography>
            )}
          />
          <FunctionField
            label="Telefone"
            sortBy="sTelResid"
            render={(record: any) => (
              <Typography
                component="span"
                sx={{
                  fontSize: 13,
                  fontFamily: fontFamilyMono,
                  whiteSpace: "nowrap",
                }}
              >
                {record.sTelResid || record.sCelular || "—"}
              </Typography>
            )}
          />
          <FunctionField
            label="Erro"
            sortBy="issueReason"
            render={(record: any) => (
              <OrphanReasonChip reason={record.issueReason} />
            )}
          />
          <FunctionField
            label="Matrícula"
            sortBy="nMatricula"
            render={(record: any) => (
              <Typography
                component="span"
                sx={{
                  fontSize: 13,
                  fontFamily: fontFamilyMono,
                  whiteSpace: "nowrap",
                }}
              >
                {formatOrphanMatricula(record) || "—"}
              </Typography>
            )}
          />
          {/* Ações visíveis (Ver sempre; Editar/Excluir só admin) no
              lugar do menu ⋮ que escondia tudo atrás de um clique */}
          <WrapperField label="Ações" textAlign="right">
            <RaRowActions show edit={isAdmin} del={isAdmin} />
          </WrapperField>
        </DatagridConfigurable>
      </List>
      {/* Explicação de como um cadastro vira membro órfão (texto herdado
          do painel antigo, reescrito no padrão visual) */}
      <Box
        sx={{
          backgroundColor: colors.bgSubtle,
          border: `1px solid ${colors.border}`,
          borderRadius: `${radius.md}px`,
          padding: "14px 18px",
          marginTop: 2,
          marginBottom: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            marginBottom: 0.75,
            color: colors.primary,
          }}
        >
          <HiOutlineInformationCircle size={18} />
          <Typography
            sx={{ fontSize: 13, fontWeight: 700, color: colors.textStrong }}
          >
            Como um cadastro vira membro órfão
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: colors.textMuted }}>
          O script de importação faz diversas sanitizações para preservar a
          integridade do banco. Esta tela lista os registros que não puderam
          ser importados:
        </Typography>
        <Box
          component="ul"
          sx={{
            margin: "6px 0 0",
            paddingLeft: "18px",
            color: colors.textMuted,
            fontSize: 12.5,
            "& li": { marginBottom: "2px" },
          }}
        >
          <li>Cadastro com a palavra VIDE no nome do sócio;</li>
          <li>Cadastro com asterisco no nome do sócio;</li>
          <li>CPF ou e-mail já cadastrado anteriormente (duplicado);</li>
          <li>
            A importação é regressiva pelo ID legado do sócio, priorizando os
            registros mais recentes.
          </li>
        </Box>
      </Box>
    </>
  );
};
